import { useEffect, useState } from "react";
import { goBack, pushScreen } from "./backstack";
import { resumeSharing } from "./location";
import BoxesApp from "./BoxesApp";
import InventoryApp from "./inventory/InventoryApp";
import Scheduler from "./scheduler/Scheduler";
import StockApp from "./stock/StockApp";
import AdminScreen from "./screens/AdminScreen";
import Launcher from "./screens/Launcher";
import MapScreen from "./screens/MapScreen";
import PinGate from "./screens/PinGate";
import { forgetPin } from "./rememberedPin";
import SmsScreen from "./screens/SmsScreen";
import VersionBadge from "./components/VersionBadge";
import { api, type Role } from "./api";

// "items" is the photo catalogue that was called Inventory until 31 Oct 2026; "stock" is the Inventory tool now, the
// stock sheet worked out (Viktor: "change the name of inventory icon that we have now to Items and create new
// Inventory module that uses this sheet as database").
export type Tool = "boxes" | "scheduler" | "items" | "stock";

// Notifications deep-link to /?tool=scheduler&task=<id>; strip it so a reload
// goes back to the launcher.
// `new=1` opens the tool's form for a new task or item (the order screen's "Create" menu, 19 Oct 2026).
// `?tool=inventory` is the old address of Items, kept so saved links and the order screen's menu still open it.
function initialRoute(): { tool: Tool | null; taskId?: number; startNew?: boolean } {
  const params = new URLSearchParams(location.search);
  const tool = params.get("tool");
  const taskId = Number(params.get("task")) || undefined;
  const startNew = params.get("new") === "1";
  if (tool) history.replaceState(null, "", location.pathname);
  if (tool === "scheduler") return { tool, taskId, startNew };
  if (tool === "items" || tool === "inventory") return { tool: "items", startNew };
  if (tool === "stock") return { tool };
  if (tool === "boxes") return { tool };
  return { tool: null };
}

export default function App() {
  const [route] = useState(initialRoute);
  // Hidden page: /sms (not linked from the launcher).
  const [path, setPath] = useState(location.pathname);
  const [tool, setTool] = useState<Tool | null>(route.tool);
  const openTool = (picked: Tool) => {
    setTool(picked);
    pushScreen(() => setTool(null));
  };
  const leaveHidden = () => {
    history.replaceState(null, "", "/");
    setPath("/");
  };

  useEffect(resumeSharing, []);

  // PIN gate: null = still checking, false = ask for the PIN, otherwise the role the PIN gave.
  const [authed, setAuthed] = useState<Role | false | null>(null);
  useEffect(() => {
    api
      .auth()
      .then((r) => setAuthed(r.ok ? (r.role ?? "full") : false))
      .catch(() => setAuthed(false));
    const again = () => setAuthed(false);
    window.addEventListener("auth-required", again);
    return () => window.removeEventListener("auth-required", again);
  }, []);

  // A notification deep link starts directly inside a tool; give it a history
  // entry too so the phone's back button goes launcher-wards, not out of the app.
  useEffect(() => {
    if (route.tool) pushScreen(() => setTool(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Forget this browser's PIN so another one (e.g. the full PIN) can be entered.
  const logout = () => {
    forgetPin();
    api
      .logout()
      .catch(() => {})
      .finally(() => setAuthed(false));
  };

  const screen =
    authed === "viewer" ? (
      // The read-only PIN: the Scheduler to look at, and nothing else.
      <Scheduler readOnly onHome={logout} initialTaskId={route.taskId} />
    ) : path === "/admin" ? (
      <AdminScreen onBack={leaveHidden} />
    ) : path === "/sms" ? (
      <SmsScreen onBack={leaveHidden} />
    ) : path === "/map" ? (
      <MapScreen onBack={leaveHidden} />
    ) : tool === "boxes" ? (
      <BoxesApp onHome={goBack} />
    ) : tool === "items" ? (
      <InventoryApp onHome={goBack} startNew={route.startNew} />
    ) : tool === "stock" ? (
      <StockApp onHome={goBack} />
    ) : tool === "scheduler" ? (
      <Scheduler onHome={goBack} initialTaskId={route.taskId} startNew={route.startNew} />
    ) : (
      <Launcher onPick={openTool} />
    );

  return (
    <>
      <VersionBadge />
      {authed === null ? null : authed ? screen : <PinGate onDone={setAuthed} />}
    </>
  );
}

